import React, { useEffect } from 'react';
import { ArrowLeft, FileText } from 'lucide-react';
import { LegalPage } from './LegalPage';
import {
  LEGAL_BASE_PATH,
  LEGAL_DOCS,
  isLegalSlug,
  isPriorVersionFolder,
  legalPath,
} from './legalConfig';
import { applyPageMeta } from './meta';

/**
 * Routing manual mínimo para `/legal/*` (el proyecto no usa react-router).
 * Se monta FUERA de AuthProvider (ver App.tsx) → páginas 100% públicas, sin
 * TenantInterceptor ni TrialGuard.
 *
 * Lee `window.location.pathname` una vez. La navegación entre páginas legales
 * usa anchors normales (recarga completa), que vuelve a evaluar la ruta — sin
 * router. Suficiente para el MVP estático.
 */
export function isLegalRoute(pathname: string): boolean {
  const p = pathname.replace(/\/+$/, '');
  return p === LEGAL_BASE_PATH || p.startsWith(`${LEGAL_BASE_PATH}/`);
}

export function LegalRouter() {
  const pathname = window.location.pathname.replace(/\/+$/, '');
  const rest = pathname.slice(LEGAL_BASE_PATH.length).replace(/^\//, '');
  const parts = rest.split('/').filter(Boolean);

  // /legal → índice
  if (parts.length === 0) return <LegalIndex />;
  // /legal/<slug> → versión vigente
  if (parts.length === 1 && isLegalSlug(parts[0])) {
    return <LegalPage documentSlug={parts[0]} />;
  }
  // /legal/v3.0/<slug> → versión histórica accesible por URL (§16)
  if (parts.length === 2) {
    const ver = isPriorVersionFolder(parts[0]);
    if (ver && isLegalSlug(parts[1])) {
      return <LegalPage documentSlug={parts[1]} version={ver} />;
    }
  }
  return <LegalUnknown />;
}

function LegalIndex() {
  useEffect(() => {
    applyPageMeta({
      title: 'Documentos legales',
      description:
        'Avisos de privacidad y términos y condiciones de FluidSol.',
      url: window.location.href,
    });
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-slate-800">
      <div className="mx-auto max-w-2xl px-5 py-14">
        <div className="mb-8 flex items-center justify-between">
          <a
            href="/"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 transition hover:text-slate-800"
          >
            <ArrowLeft className="h-4 w-4" /> Volver
          </a>
          <span className="font-serif text-lg tracking-tight text-slate-900">
            FluidSol
          </span>
        </div>

        <h1 className="font-serif text-2xl tracking-tight text-slate-900">
          Documentos legales
        </h1>
        <p className="mt-2 text-sm text-slate-500">
          Información sobre el tratamiento de datos personales y las condiciones
          de uso del servicio.
        </p>

        <ul className="mt-6 space-y-3">
          {LEGAL_DOCS.map((doc) => (
            <li key={doc.slug}>
              <a
                href={legalPath(doc.slug)}
                className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 transition hover:border-slate-300 hover:bg-slate-50"
              >
                <FileText className="h-4 w-4 text-slate-400" />
                <span className="text-sm font-medium text-slate-800">
                  {doc.title}
                </span>
              </a>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function LegalUnknown() {
  useEffect(() => {
    applyPageMeta({
      title: 'Documento no encontrado',
      description: 'El documento legal solicitado no existe.',
      url: window.location.href,
    });
  }, []);

  return (
    <div className="grid min-h-screen place-items-center bg-[#FAFAF7] px-5 text-center">
      <div>
        <h1 className="font-serif text-2xl text-slate-900">
          Documento no encontrado
        </h1>
        <p className="mt-2 text-sm text-slate-500">
          El documento legal solicitado no existe.
        </p>
        <div className="mt-5 flex items-center justify-center gap-3">
          <a
            href={LEGAL_BASE_PATH}
            className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Ver documentos legales
          </a>
          <a
            href="/"
            className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
          >
            Ir al inicio
          </a>
        </div>
      </div>
    </div>
  );
}

export default LegalRouter;
